import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import api from '../services/api';

const CLIENTS_CACHE_TIME = 5 * 60 * 1000; // 5 minutes
const ACTIVITIES_CACHE_TIME = 2 * 60 * 1000; // 2 minutes

// Shared helpers (mirror backend) — used for optimistic updates
export const calcPhaseProgress = (activities, deliverables) => {
  const total = (activities?.length || 0) + (deliverables?.length || 0);
  if (!total) return 0;
  const done = (activities?.filter((a) => a.completed).length || 0) +
               (deliverables?.filter((d) => d.status === 'delivered').length || 0);
  return Math.round((done / total) * 100);
};

export const inferPhaseStatus = (activities, deliverables) => {
  const acts = activities || [];
  const dels = deliverables || [];
  const total = acts.length + dels.length;
  if (total === 0) return 'not_started';
  const done = acts.filter((a) => a.completed).length + dels.filter((d) => d.status === 'delivered').length;
  if (done === total) return 'completed';
  const hasProgress = acts.some((a) => a.completed) || dels.some((d) => d.status !== 'pending');
  return hasProgress ? 'in_progress' : 'not_started';
};

export function useClients({ enabled = true } = {}) {
  // Use the same /api/companies endpoint — clients and companies are the same thing.
  // Shares the cache with useCompanies({ limit: 200 }).
  return useQuery({
    queryKey: ['companies', { limit: 200 }],
    queryFn: async () => {
      const response = await api.get('/companies', { params: { limit: 200 } });
      return response.data;
    },
    staleTime: CLIENTS_CACHE_TIME,
    gcTime: CLIENTS_CACHE_TIME * 2,
    enabled,
  });
}

export function useClient(id) {
  return useQuery({
    queryKey: ['clients', id],
    queryFn: async () => {
      const response = await api.get(`/clients/${id}`);
      return response.data;
    },
    staleTime: CLIENTS_CACHE_TIME,
    cacheTime: CLIENTS_CACHE_TIME * 2,
    enabled: !!id,
  });
}

export function useClientPhases(id) {
  return useQuery({
    queryKey: ['clients', id, 'phases'],
    queryFn: async () => {
      const response = await api.get(`/clients/${id}/phases`);
      return response.data;
    },
    staleTime: CLIENTS_CACHE_TIME,
    cacheTime: CLIENTS_CACHE_TIME * 2,
    enabled: !!id,
  });
}

export function useClientDocuments(id) {
  return useQuery({
    queryKey: ['clients', id, 'documents'],
    queryFn: async () => {
      const response = await api.get(`/clients/${id}/documents`);
      return response.data;
    },
    staleTime: CLIENTS_CACHE_TIME,
    cacheTime: CLIENTS_CACHE_TIME * 2,
    enabled: !!id,
  });
}

export function useClientWorkshops(id) {
  return useQuery({
    queryKey: ['clients', id, 'workshops'],
    queryFn: async () => {
      const response = await api.get(`/clients/${id}/workshops`);
      return response.data;
    },
    staleTime: CLIENTS_CACHE_TIME,
    cacheTime: CLIENTS_CACHE_TIME * 2,
    enabled: !!id,
  });
}

export function useRecentActivities(limit = 20) {
  return useQuery({
    queryKey: ['activities', limit],
    queryFn: async () => {
      const response = await api.get('/activities', { params: { limit } });
      return response.data.activities ?? response.data;
    },
    staleTime: ACTIVITIES_CACHE_TIME,
    cacheTime: ACTIVITIES_CACHE_TIME * 2,
  });
}

export function useActivitiesPaginated({ companyId } = {}) {
  return useInfiniteQuery({
    queryKey: ['activities-paginated', { companyId: companyId || null }],
    queryFn: async ({ pageParam }) => {
      const params = { limit: 50 };
      if (companyId) params.companyId = companyId;
      if (pageParam) params.cursor = pageParam;
      const res = await api.get('/activities', { params });
      return res.data;
    },
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage.hasMore ? lastPage.nextCursor : undefined,
    staleTime: ACTIVITIES_CACHE_TIME,
  });
}

export function useUpcomingTasks() {
  return useQuery({
    queryKey: ['upcoming-tasks'],
    queryFn: async () => {
      const response = await api.get('/clients/upcoming-tasks');
      return response.data;
    },
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
}

export function usePracticeMetrics() {
  return useQuery({
    queryKey: ['practice-metrics'],
    queryFn: async () => {
      const response = await api.get('/dashboard/practice-metrics');
      return response.data;
    },
    staleTime: ACTIVITIES_CACHE_TIME,
    cacheTime: ACTIVITIES_CACHE_TIME * 2,
  });
}

export function useCreateClient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => api.post('/clients', data).then((r) => r.data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      queryClient.invalidateQueries({ queryKey: ['practice-metrics'] });
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      if (variables?.companyId) {
        queryClient.invalidateQueries({ queryKey: ['clients', variables.companyId, 'phases'] });
      }
    },
  });
}

export function useUpdateClientConsulting() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }) => api.put(`/clients/${id}/consulting`, data).then((r) => r.data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['clients', id] });
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      queryClient.invalidateQueries({ queryKey: ['practice-metrics'] });
    },
  });
}

export function useUpdatePhase() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ clientId, phaseId, ...data }) =>
      api.put(`/clients/${clientId}/phases/${phaseId}`, data).then((r) => r.data),
    onMutate: async ({ clientId, phaseId, ...updates }) => {
      await queryClient.cancelQueries({ queryKey: ['clients', clientId, 'phases'] });
      const prev = queryClient.getQueryData(['clients', clientId, 'phases']);
      queryClient.setQueryData(['clients', clientId, 'phases'], (old) => {
        if (!old) return old;
        return old.map((p) => {
          if (p.phaseId !== phaseId) return p;
          const merged = { ...p, ...updates };
          merged.progress = calcPhaseProgress(merged.activities, merged.deliverables);
          merged.status = inferPhaseStatus(merged.activities, merged.deliverables);
          return merged;
        });
      });
      return { prev };
    },
    onError: (_, variables, context) => {
      if (context?.prev) {
        queryClient.setQueryData(['clients', variables.clientId, 'phases'], context.prev);
      }
    },
    onSettled: (_, __, variables) => {
      queryClient.invalidateQueries({ queryKey: ['clients', variables.clientId, 'phases'] });
      queryClient.invalidateQueries({ queryKey: ['practice-metrics'] });
      queryClient.invalidateQueries({ queryKey: ['upcoming-tasks'] });
    },
  });
}

export function useAddPhase() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ clientId, ...data }) =>
      api.post(`/clients/${clientId}/phases`, data).then((r) => r.data),
    onSuccess: (_, { clientId }) => {
      queryClient.invalidateQueries({ queryKey: ['clients', clientId, 'phases'] });
      queryClient.invalidateQueries({ queryKey: ['upcoming-tasks'] });
    },
  });
}

export function useDeletePhase() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ clientId, phaseId }) =>
      api.delete(`/clients/${clientId}/phases/${phaseId}`).then((r) => r.data),
    onMutate: async ({ clientId, phaseId }) => {
      await queryClient.cancelQueries({ queryKey: ['clients', clientId, 'phases'] });
      const prev = queryClient.getQueryData(['clients', clientId, 'phases']);
      queryClient.setQueryData(['clients', clientId, 'phases'], (old) => {
        if (!old) return old;
        return old
          .filter((p) => p.phaseId !== phaseId)
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
          .map((p, idx) => ({ ...p, order: idx + 1 }));
      });
      return { prev };
    },
    onError: (_, variables, context) => {
      if (context?.prev) {
        queryClient.setQueryData(['clients', variables.clientId, 'phases'], context.prev);
      }
    },
    onSettled: (_, __, variables) => {
      queryClient.invalidateQueries({ queryKey: ['clients', variables.clientId, 'phases'] });
      queryClient.invalidateQueries({ queryKey: ['practice-metrics'] });
      queryClient.invalidateQueries({ queryKey: ['upcoming-tasks'] });
    },
  });
}

export function useAddDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ clientId, ...data }) =>
      api.post(`/clients/${clientId}/documents`, data).then((r) => r.data),
    onSuccess: (_, { clientId }) => {
      queryClient.invalidateQueries({ queryKey: ['clients', clientId, 'documents'] });
      queryClient.invalidateQueries({ queryKey: ['practice-metrics'] });
      queryClient.invalidateQueries({ queryKey: ['activities'] });
    },
  });
}

export function useUpdateDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ clientId, docId, ...data }) =>
      api.put(`/clients/${clientId}/documents/${docId}`, data).then((r) => r.data),
    onSuccess: (_, { clientId }) => {
      queryClient.invalidateQueries({ queryKey: ['clients', clientId, 'documents'] });
      queryClient.invalidateQueries({ queryKey: ['activities-paginated'] });
    },
  });
}

export function useDeleteDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ clientId, docId }) =>
      api.delete(`/clients/${clientId}/documents/${docId}`).then((r) => r.data),
    onSuccess: (_, { clientId }) => {
      queryClient.invalidateQueries({ queryKey: ['clients', clientId, 'documents'] });
      queryClient.invalidateQueries({ queryKey: ['practice-metrics'] });
      queryClient.invalidateQueries({ queryKey: ['activities-paginated'] });
    },
  });
}

// ─── Engagement Template hooks ───────────────────────────────────

export function useEngagementTemplates() {
  return useQuery({
    queryKey: ['engagement-templates'],
    queryFn: async () => {
      const response = await api.get('/engagement-templates');
      return response.data;
    },
    staleTime: CLIENTS_CACHE_TIME,
  });
}

export function useCreateTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => api.post('/engagement-templates', data).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['engagement-templates'] });
    },
  });
}

export function useUpdateTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }) => api.put(`/engagement-templates/${id}`, data).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['engagement-templates'] });
    },
  });
}

export function useDeleteTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.delete(`/engagement-templates/${id}`).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['engagement-templates'] });
    },
  });
}

export function useApplyTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ clientId, templateId }) =>
      api.post(`/clients/${clientId}/apply-template`, { templateId }).then((r) => r.data),
    onSuccess: (_, { clientId }) => {
      queryClient.invalidateQueries({ queryKey: ['clients', clientId, 'phases'] });
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      queryClient.invalidateQueries({ queryKey: ['practice-metrics'] });
      queryClient.invalidateQueries({ queryKey: ['activities'] });
    },
  });
}

// ─── Survey hooks ────────────────────────────────────────────────

export function useAllSurveys() {
  return useQuery({
    queryKey: ['surveys', 'all'],
    queryFn: async () => {
      const response = await api.get('/surveys');
      return response.data;
    },
    staleTime: 60 * 1000,
  });
}

export function useClientSurveys(id) {
  return useQuery({
    queryKey: ['clients', id, 'surveys'],
    queryFn: async () => {
      const response = await api.get(`/clients/${id}/surveys`);
      return response.data;
    },
    staleTime: CLIENTS_CACHE_TIME,
    cacheTime: CLIENTS_CACHE_TIME * 2,
    enabled: !!id,
  });
}

export function useCreateSurvey() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ clientId, ...data }) =>
      api.post(`/clients/${clientId}/surveys`, data).then((r) => r.data),
    onSuccess: (_, { clientId }) => {
      queryClient.invalidateQueries({ queryKey: ['clients', clientId, 'surveys'] });
      queryClient.invalidateQueries({ queryKey: ['activities-paginated'] });
    },
  });
}

export function useUpdateSurvey() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ clientId, surveyId, ...data }) =>
      api.patch(`/clients/${clientId}/surveys/${surveyId}`, data).then((r) => r.data),
    onSuccess: (_, { clientId }) => {
      queryClient.invalidateQueries({ queryKey: ['clients', clientId, 'surveys'] });
      queryClient.invalidateQueries({ queryKey: ['activities-paginated'] });
    },
  });
}

export function useDeleteSurvey() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ clientId, surveyId }) =>
      api.delete(`/clients/${clientId}/surveys/${surveyId}`).then((r) => r.data),
    onSuccess: (_, { clientId }) => {
      queryClient.invalidateQueries({ queryKey: ['clients', clientId, 'surveys'] });
      queryClient.invalidateQueries({ queryKey: ['activities-paginated'] });
    },
  });
}

export function useCompileSurvey() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ clientId, surveyId }) =>
      api.post(`/clients/${clientId}/surveys/${surveyId}/compile`).then((r) => r.data),
    onSuccess: (_, { clientId }) => {
      queryClient.invalidateQueries({ queryKey: ['clients', clientId, 'surveys'] });
      queryClient.invalidateQueries({ queryKey: ['surveys', 'all'] });
      queryClient.invalidateQueries({ queryKey: ['practice-metrics'] });
      queryClient.invalidateQueries({ queryKey: ['activities-paginated'] });
    },
  });
}

export function useToggleSurveyActive() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ clientId, surveyId, active }) =>
      api.post(`/clients/${clientId}/surveys/${surveyId}/set-active`, { active }).then((r) => r.data),
    onSuccess: (_, { clientId }) => {
      queryClient.invalidateQueries({ queryKey: ['clients', clientId, 'surveys'] });
      queryClient.invalidateQueries({ queryKey: ['analytics'] });
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      queryClient.invalidateQueries({ queryKey: ['activities-paginated'] });
    },
  });
}
